import { Col, Divider, Form, Input, Row, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { AttachmentInput } from './AttachmentInput'
import { DeviceTypeSelect } from './DeviceTypeSelect'
import { InstallerInput } from './InstallerInput'
import { LocationPicker } from './LocationPicker'
import { CUSTOMER_TYPES, DEVICE_OWNERSHIPS, type SubscriberProfile } from './profileApi'
import { useRequiredFields, type FormFieldKey } from './formSettingsApi'
import { useMapFallback } from './profileForm'
import { PHONE_E164, normalizePhone } from './subscriberApi'

interface SubscriberProfileFieldsProps {
  mode: 'create' | 'edit'
  /** Tahrirlashda — joriy qiymatlar (fayl bor-yo'qligi, niqoblangan WhatsApp) */
  current?: SubscriberProfile | null
}

export function SubscriberProfileFields({ mode, current }: SubscriberProfileFieldsProps) {
  const { t } = useTranslation()
  const create = mode === 'create'
  const fallback = useMapFallback()
  const requiredFields = useRequiredFields(create)
  const required = (key: FormFieldKey, label: string) =>
    create && requiredFields.has(key) ? [{ required: true, message: t('subscriber.profile.required', { field: label }) }] : []

  const labels = {
    firstName: t('subscriber.profile.firstName'),
    fatherName: t('subscriber.profile.fatherName'),
    lastName: t('subscriber.profile.lastName'),
    deviceType: t('subscriber.profile.deviceType'),
    deviceOwnership: t('subscriber.profile.deviceOwnership'),
    customerType: t('subscriber.profile.customerType'),
    whatsapp: t('subscriber.profile.whatsapp'),
    photo: t('subscriber.profile.photo'),
    contract: t('subscriber.profile.contract'),
    installedBy: t('subscriber.profile.installedBy'),
    location: t('subscriber.profile.location'),
  }

  return (
    <>
      <Divider orientation="left" plain style={{ marginTop: 0 }}>
        {t('subscriber.profile.section')}
      </Divider>

      <Row gutter={12}>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'firstName']} label={labels.firstName} rules={[...required('firstName', labels.firstName), { max: 100 }]}>
            <Input autoComplete="off" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'fatherName']} label={labels.fatherName} rules={[...required('fatherName', labels.fatherName), { max: 100 }]}>
            <Input autoComplete="off" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'lastName']} label={labels.lastName} rules={[...required('lastName', labels.lastName), { max: 100 }]}>
            <Input autoComplete="off" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'customerType']} label={labels.customerType} rules={required('customerType', labels.customerType)}>
            <Select
              options={CUSTOMER_TYPES.map((v) => ({ value: v, label: t(`subscriber.customerType.${v}`) }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'deviceTypeId']} label={labels.deviceType} rules={required('deviceTypeId', labels.deviceType)}>
            <DeviceTypeSelect currentName={current?.deviceTypeName} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'deviceOwnership']} label={labels.deviceOwnership} rules={required('deviceOwnership', labels.deviceOwnership)}>
            <Select
              options={DEVICE_OWNERSHIPS.map((v) => ({ value: v, label: t(`subscriber.deviceOwnership.${v}`) }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name={['profile', 'whatsapp']}
        label={labels.whatsapp}
        extra={
          !create && current?.whatsapp
            ? t('subscriber.profile.whatsappKeep', { value: current.whatsapp })
            : t('subscriber.profile.whatsappHint')
        }
        rules={[
          ...required('whatsapp', labels.whatsapp),
          {
            validator: (_, value: string | undefined) => {
              const clean = normalizePhone(value)
              if (clean === null || PHONE_E164.test(clean)) return Promise.resolve()
              return Promise.reject(new Error(t('subscriber.profile.whatsappFormat')))
            },
          },
        ]}
      >
        <Input autoComplete="off" inputMode="tel" placeholder={!create ? (current?.whatsapp ?? undefined) : undefined} />
      </Form.Item>

      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item name={['profile', 'photo']} label={labels.photo} rules={required('photo', labels.photo)} extra={t('subscriber.profile.photoHint')}>
            <AttachmentInput kind="PHOTO" existing={!create && Boolean(current?.photoFileId)} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name={['profile', 'contract']} label={labels.contract} rules={required('contract', labels.contract)} extra={t('subscriber.profile.contractHint')}>
            <AttachmentInput kind="CONTRACT" existing={!create && Boolean(current?.contractFileId)} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name={['profile', 'installer']}
        label={labels.installedBy}
        extra={t('subscriber.profile.installedByHint')}
        rules={required('installedBy', labels.installedBy)}
      >
        <InstallerInput />
      </Form.Item>

      <Form.Item name={['profile', 'location']} label={labels.location} rules={required('location', labels.location)}>
        <LocationPicker fallbackCenter={fallback} />
      </Form.Item>
    </>
  )
}
